import ReactGoogleAutocomplete from "react-google-autocomplete";
import {
  Controller,
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  FieldPathValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

const { REACT_APP_GOOGLE_API_KEY: GOOGLE_API_KEY } = process.env;

interface AdreessInputProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  label?: string;
  required?: boolean;
  shouldUnregister?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  defaultValue?: FieldPathValue<FieldsType, Path<FieldsType>>;
  onAddressSelect: (AddressDetails) => void;
}

export interface AddressDetails {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const AdreessInput = <FieldsType extends FieldValues>({
  placeholder,
  label,
  name,
  control,
  required,
  shouldUnregister,
  requiredMessage,
  rules,
  defaultValue,
  onAddressSelect,
}: AdreessInputProps<FieldsType>) => {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      shouldUnregister={shouldUnregister}
      rules={{
        required: requiredMessage || (required && "Required"),
        ...(rules || {}),
      }}
      defaultValue={defaultValue}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className="relative">
          {label && (
            <label
              className="text-base ml-1 mb-0.5 block"
              htmlFor={field?.name}
            >
              <span>
                {t(label)}
                {(required || requiredMessage) && (
                  <span className="text-red-600 ml-0.5 text-base">*</span>
                )}
              </span>
            </label>
          )}
          <ReactGoogleAutocomplete
            id={field.name}
            apiKey={GOOGLE_API_KEY}
            {...field}
            onPlaceSelected={(place) => {
              const address = extractAddress(place);
              onAddressSelect(address);
              field.onChange(address.address);
            }}
            language="en"
            options={{
              fields: ["address_components"],
              componentRestrictions: { country: ["us"] },
              types: ["address"],
            }}
            placeholder={t(placeholder || label)}
            className="placeholder:text-gray-500 border-gray-200 rounded-lg w-full px-2.5 py-3 border focus:border-brand-hover
            focus-within:border-brand-hover focus:bg-[#eaf1fa]  transition-all focus:ring-0 focus-visible:ring-0 focus-visible:border-brand-hover  outline-none"
          />
          <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1  bottom-0 left-0 right-0">
            {t(error?.message)}
          </p>
        </div>
      )}
    />
  );
};

export default AdreessInput;

const extractAddress = (place): AddressDetails => {
  const addressDetails: AddressDetails = {
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  };
  const addressMapping = {
    street_number: (component) => {
      addressDetails.address = `${component.long_name} ${addressDetails.address}`;
    },
    route: (component) => {
      addressDetails.address += component.short_name;
    },
    postal_code: (component) => {
      addressDetails.zipCode = `${component.long_name}${addressDetails.zipCode}`;
    },
    postal_code_suffix: (component) => {
      addressDetails.zipCode = `${addressDetails.zipCode}-${component.long_name}`;
    },
    locality: (component) => {
      addressDetails.city = component.long_name;
    },
    administrative_area_level_1: (component) => {
      addressDetails.state = component.short_name;
    },
    country: (component) => {
      addressDetails.country = component.long_name;
    },
  };

  const addressComponents = place?.address_components || [];
  for (const component of addressComponents) {
    const componentType = component.types[0];
    if (addressMapping[componentType]) {
      addressMapping[componentType](component);
    }
  }
  return addressDetails;
};
